import { useRef, useState } from 'react';
import { Button, Icon, Popover } from '../ui';
import type { HostingRun } from '../../lib/hosting';

export interface RunButtonProps {
  /** Why the account can't run, or null when it can. */
  disabledReason: string | null;
  latest: HostingRun | null;
  busy: boolean;
  onRun: (updates: boolean) => void;
  onCancel: () => void;
}

const MODES = [
  { updates: true, label: 'Run checklist', hint: 'Applies WordPress updates' },
  { updates: false, label: 'Check only (no updates)', hint: 'Lists updates without applying them' },
];

/**
 * The detail header's Run control: a split button (run, or check only) while
 * idle; "Running…" or "Queued" with Cancel while a run is live.
 */
export default function RunButton({ disabledReason, latest, busy, onRun, onCancel }: RunButtonProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const caretRef = useRef<HTMLSpanElement>(null);

  if (latest?.status === 'running') {
    return (
      <Button size="sm" variant="secondary" icon="refresh-cw" disabled title="The checklist is running">
        Running…
      </Button>
    );
  }
  if (latest?.status === 'queued') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
        <Button size="sm" variant="secondary" icon="clock" disabled title="Waiting for the runner to pick it up">
          Queued
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </span>
    );
  }

  const disabled = busy || Boolean(disabledReason);
  return (
    // A disabled button swallows hover, so the reason sits on the wrapper.
    <span title={disabledReason ?? undefined} style={{ display: 'inline-flex' }}>
      <Button
        size="sm"
        icon="play"
        disabled={disabled}
        onClick={() => onRun(true)}
        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
      >
        {busy ? 'Queuing…' : 'Run checklist'}
      </Button>
      <span ref={caretRef} style={{ display: 'inline-flex' }}>
        <Button
          size="sm"
          disabled={disabled}
          aria-label="More run options"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
          style={{
            padding: '0 7px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: '1px solid color-mix(in srgb, var(--text-on-accent) 28%, transparent)',
          }}
        >
          <Icon name="chevron-down" size={14} />
        </Button>
      </span>
      <Popover open={menuOpen} anchorEl={caretRef.current} onClose={() => setMenuOpen(false)} align="right" minWidth={248} role="menu" style={{ display: 'flex', flexDirection: 'column' }}>
        {MODES.map((m) => (
          <button
            key={m.label}
            type="button"
            role="menuitem"
            className="hover-wash pressable"
            onClick={() => {
              setMenuOpen(false);
              onRun(m.updates);
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '2px',
              padding: '8px 10px',
              border: 'none',
              background: 'transparent',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'var(--font-ui)',
            }}
          >
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{m.label}</span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{m.hint}</span>
          </button>
        ))}
      </Popover>
    </span>
  );
}
